import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Smile, Meh, Frown } from "lucide-react";
import { hora12 } from "@/lib/dateFormat";

const cara = (score) => {
  if (score === 3) return <Smile className="h-4 w-4 text-emerald-600" />;
  if (score === 2) return <Meh className="h-4 w-4 text-amber-600" />;
  return <Frown className="h-4 w-4 text-rose-600" />;
};

const Barras = ({ b }) => (
  <div className="flex items-center gap-3 text-xs text-slate-500">
    <span className="flex items-center gap-1"><Smile className="h-3.5 w-3.5 text-emerald-600" />{b?.["3"] || 0}</span>
    <span className="flex items-center gap-1"><Meh className="h-3.5 w-3.5 text-amber-600" />{b?.["2"] || 0}</span>
    <span className="flex items-center gap-1"><Frown className="h-3.5 w-3.5 text-rose-600" />{b?.["1"] || 0}</span>
  </div>
);

/** Cómo les están gustando las clases de WSS Live. */
export const LiveRatingsPanel = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/live-sessions/ratings/summary").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  if (!data.overall?.count) {
    return <div className="text-sm text-slate-500" data-testid="live-ratings-empty">{t("live_ratings_none")}</div>;
  }

  return (
    <div className="space-y-6" data-testid="live-ratings-panel">
      <div className="flex flex-wrap items-center gap-6 rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">{t("live_ratings_overall")}</div>
          <div className="text-3xl font-bold text-slate-900" data-testid="live-ratings-loved">
            {data.overall.loved_pct}%
          </div>
          <div className="text-xs text-slate-500">{t("live_rate_loved")}</div>
        </div>
        <div className="space-y-1.5">
          <Barras b={data.overall.breakdown} />
          <div className="text-xs text-slate-500" data-testid="live-ratings-count">
            {data.overall.count} · {t("live_ratings_votes")}
          </div>
        </div>
      </div>

      {data.by_teacher?.length > 0 && (
        <div>
          <div className="mb-2 text-sm font-semibold text-slate-700">{t("live_ratings_by_teacher")}</div>
          <div className="space-y-1.5">
            {data.by_teacher.map((p) => (
              <div key={p.teacher_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                   data-testid={`live-ratings-teacher-${p.teacher_id}`}>
                <span className="text-sm text-slate-700">{p.teacher_name}</span>
                <div className="flex items-center gap-4">
                  <Barras b={p.breakdown} />
                  <span className="text-sm font-semibold text-slate-900">{p.loved_pct}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="mb-2 text-sm font-semibold text-slate-700">{t("live_ratings_by_session")}</div>
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {data.by_session.map((s) => (
            <div key={s.session_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                 data-testid={`live-ratings-session-${s.session_id}`}>
              <div className="min-w-0 pr-3">
                <div className="truncate text-sm text-slate-800">{s.title}</div>
                <div className="text-xs text-slate-500">
                  {s.date} {s.start_time ? hora12(s.start_time) : ""}{s.teacher_name ? ` · ${s.teacher_name}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Barras b={s.breakdown} />
                <span className="text-sm font-semibold text-slate-900">{s.loved_pct}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.comments?.length > 0 && (
        <div>
          <div className="mb-1 text-sm font-semibold text-slate-700">{t("live_ratings_comments")}</div>
          {!data.with_names && <div className="mb-2 text-xs text-slate-400">{t("live_ratings_anon")}</div>}
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="live-ratings-comments">
            {data.comments.map((c, i) => (
              <div key={`${c.session_id}-${i}`} className="rounded-lg border border-slate-200 px-3 py-2">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  {cara(c.score)} {c.title} · {c.date}{c.user_name ? ` · ${c.user_name}` : ""}
                </div>
                <div className="mt-1 text-sm text-slate-800">{c.comment}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveRatingsPanel;
